import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParticipantNotes } from "../../../../../../hooks/useParticipantNotes";
import { usePinParticipantNote } from "../../../../../../hooks/usePinParticipantNote";
import NotesHistoryState from "./NotesHistoryState";
import NotesHistoryCoachNote from "./NotesHistoryCoachNote";
import NotesHistoryWellnessCheckNote from "./NotesHistoryWellnessCheckNote";
import NotesHistoryOtherNote from "./NotesHistoryOtherNote";
import { Formatter } from "../../../../../../services/formatter/formatter.service";
import {
	parseWellnessCheckFromSubjective,
	NoteHelper,
} from "../../../../../../services/helpers/note-helper";
import { useSmartGoals } from "../../../../../../hooks/custom-goals/useSmartGoals";
import { Note, NoteTopics } from "../../../../../../models/notes/note";
import {
	formatWellnessCheckGroupLabel,
	isWellnessType,
} from "../../../../../../models/notes/wellness-check-additional-categories.model";
import { Form } from "react-final-form";
import UiSelectMultiple from "../../../../../../components/select-multiple/SelectMultiple.component";
import { ListOption } from "../../../../../../models/misc.model";

const sortNotes = (notes: Note[]): Note[] => {
	return [...notes].sort((a, b) => {
		if (a.pinned && !b.pinned) return -1;
		if (!a.pinned && b.pinned) return 1;
		return new Date(b.date).getTime() - new Date(a.date).getTime();
	});
};

const NotesHistory = ({
	participantId,
	topicsFilter,
	updatingNote,
	notesQuery,
	smartGoalsQuery,
	onUpdate,
	onTopicsFilter,
}: {
	participantId: number;
	topicsFilter: { filter: NoteTopics[] };
	updatingNote: Note | null;
	notesQuery: ReturnType<typeof useParticipantNotes>;
	smartGoalsQuery: ReturnType<typeof useSmartGoals>;
	onUpdate: (note: Note) => void;
	onTopicsFilter: (e: NoteTopics[]) => void;
}) => {
	const { t } = useTranslation("common");
	const pinMutation = usePinParticipantNote({ participantId });

	const filteredAndSortedNotes = useMemo(() => {
		if (!notesQuery.data?.length) return [];
		const filtered = notesQuery.data.filter((x) => {
			if (isWellnessType(x.type)) return true;
			if (topicsFilter.filter.length > 0) {
				return x.topics?.some((y) => topicsFilter.filter.includes(y));
			}
			return true;
		});
		return sortNotes(filtered);
	}, [notesQuery.data, topicsFilter.filter]);

	// Loading / Error / No data
	if (notesQuery.isLoading || notesQuery.isError || !notesQuery.data?.length) {
		return (
			<NotesHistoryState
				isLoading={notesQuery.isLoading}
				isError={notesQuery.isError}
				isEmpty={notesQuery.data?.length ? false : true}
				onRefresh={notesQuery.invalidate}
			/>
		);
	}

	const handleTogglePin = (note: Note) => {
		pinMutation.mutate({ noteId: note.id, pinned: !note.pinned });
	};

	// Data
	return (
		<div className="participant-notes-history">
			<div className="row">
				<div className="col-12 col-sm-6 col-md-6 col-lg-6 participant-notes-history__title">
					{t(`PARTICIPANT.DASHBOARD.NOTES.HISTORY_TABLE_TITLE`)}
				</div>
				<div className="col-12 col-sm-6 col-md-6 col-lg-6 participant-notes-history__filter">
					<Form<{ filter: NoteTopics[] }>
						initialValues={topicsFilter}
						onSubmit={(values) => onTopicsFilter(values.filter)}
						render={({ handleSubmit, submitting, form }) => (
							<form onSubmit={handleSubmit}>
								<UiSelectMultiple
									id="filter"
									name="filter"
									placeholder="PARTICIPANT.DASHBOARD.NOTES.TOPICS"
									onChange={(e) => onTopicsFilter(e.target.value)}
									options={Object.keys(NoteTopics).map(
										(x) =>
											new ListOption({
												id: x,
												label: `PARTICIPANT.DASHBOARD.NOTES.TOPICS_SPOKEN_OPTIONS.${x}`,
											})
									)}
									removeBottomSpacer={true}
									panelHeaderTemplate={<></>}
								/>
							</form>
						)}
					/>
				</div>
			</div>

			{filteredAndSortedNotes.map((note) => {
				const topics = (note.topics ?? [])
					.map((topic) => t(`PARTICIPANT.DASHBOARD.NOTES.TOPICS_SPOKEN_OPTIONS.${topic}`))
					.join(", ");

				const lastEdit = NoteHelper.lastEdit(note);

				return note.type === "COACHING" ? (
					<NotesHistoryCoachNote
						key={note.id}
						date={note.date}
						callerName={Formatter.fullName(note.coach.firstName, note.coach.lastName)}
						editing={note.id === updatingNote?.id}
						pinned={note.pinned}
						pinLoading={pinMutation.isPending}
						onTogglePin={() => handleTogglePin(note)}
						subjective={note.subjective ?? undefined}
						objective={note.objective ?? undefined}
						assessment={note.assessment ?? undefined}
						stateOfChange={
							note.stateOfChange
								? t(
										`PARTICIPANT.DASHBOARD.NOTES.STATE_OF_CHANGE_OPTIONS.${note.stateOfChange}`
									)
								: undefined
						}
						plan={note.plan ?? undefined}
						topics={topics && topics !== "" ? topics : undefined}
						noteProgress={note.noteProgress}
						smartGoalsQuery={smartGoalsQuery}
						lastEditionDate={lastEdit?.on}
						lastEditedBy={lastEdit?.by}
						onClickEdit={() => onUpdate(note)}
					/>
				) : isWellnessType(note.type) ? (
					<NotesHistoryWellnessCheckNote
						key={note.id}
						date={note.date}
						typeLabel={formatWellnessCheckGroupLabel(note.type)}
						callerName={Formatter.fullName(note.coach.firstName, note.coach.lastName)}
						editing={note.id === updatingNote?.id}
						pinned={note.pinned}
						pinLoading={pinMutation.isPending}
						onTogglePin={() => handleTogglePin(note)}
						wellnessCheckItems={
							note.subjective
								? parseWellnessCheckFromSubjective(note.subjective)
								: (note.wellnessCheckItems ?? [])
						}
						lastEditionDate={lastEdit?.on}
						lastEditedBy={lastEdit?.by}
						onClickEdit={() => onUpdate(note)}
					/>
				) : (
					<NotesHistoryOtherNote
						key={note.id}
						date={note.date}
						callerName={Formatter.fullName(note.coach.firstName, note.coach.lastName)}
						editing={note.id === updatingNote?.id}
						pinned={note.pinned}
						pinLoading={pinMutation.isPending}
						onTogglePin={() => handleTogglePin(note)}
						subjective={note.subjective || undefined}
						topics={topics && topics !== "" ? topics : undefined}
						lastEditionDate={lastEdit?.on}
						lastEditedBy={lastEdit?.by}
						onClickEdit={() => onUpdate(note)}
					/>
				);
			})}
		</div>
	);
};

export default NotesHistory;
